import { useState } from 'react'
import {
  Car,
  Bike,
  HeartPulse,
  Flame,
  ShieldCheck,
  Wallet,
  Users,
  Calculator,
  ChevronLeft,
  Wifi,
  BatteryFull,
  SignalHigh,
  FileText,
  Share2,
} from 'lucide-react'

/**
 * InteractiveDevice — a realistic iPhone 15 Pro mockup (titanium frame,
 * Dynamic Island, physical side buttons) running a CLICKABLE demo of the
 * BimaUncle app in the app's own design language. Visitors tap calculators,
 * see a sample quote, and switch tabs — so the site feels like the product.
 * Pure CSS/React, no deps. (No insurer names; no payout/commission claims.)
 */

type Tab = 'calc' | 'wallet' | 'quotes' | 'profile'

const LOBS = [
  { key: 'motor', label: 'Private Car', icon: Car, premium: '₹12,480', tone: 'from-blue-500 to-blue-600' },
  { key: 'tw', label: 'Two-Wheeler', icon: Bike, premium: '₹2,190', tone: 'from-indigo-500 to-indigo-600' },
  { key: 'health', label: 'Health', icon: HeartPulse, premium: '₹18,600', tone: 'from-rose-500 to-rose-600' },
  { key: 'fire', label: 'Fire', icon: Flame, premium: '₹9,750', tone: 'from-orange-500 to-orange-600' },
  { key: 'pa', label: 'Personal Accident', icon: ShieldCheck, premium: '₹1,499', tone: 'from-emerald-500 to-emerald-600' },
  { key: 'eng', label: 'Engineering', icon: Calculator, premium: '₹24,300', tone: 'from-violet-500 to-violet-600' },
]

export function InteractiveDevice() {
  const [tab, setTab] = useState<Tab>('calc')
  const [selected, setSelected] = useState<(typeof LOBS)[number] | null>(null)

  return (
    <div className="relative mx-auto w-full max-w-[300px]">
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-12 -z-10 rounded-[60px] bg-[radial-gradient(50%_50%_at_50%_40%,rgba(0,82,204,0.30),rgba(88,86,214,0.18),transparent_75%)] blur-3xl"
      />

      <div className="animate-float relative">
        {/* physical side buttons */}
        <div aria-hidden className="absolute left-[-3px] top-[108px] h-7 w-[3px] rounded-l bg-[#2a2a2c]" />
        <div aria-hidden className="absolute left-[-3px] top-[150px] h-12 w-[3px] rounded-l bg-[#2a2a2c]" />
        <div aria-hidden className="absolute left-[-3px] top-[208px] h-12 w-[3px] rounded-l bg-[#2a2a2c]" />
        <div aria-hidden className="absolute right-[-3px] top-[170px] h-16 w-[3px] rounded-r bg-[#2a2a2c]" />

        {/* iPhone 15 Pro — titanium frame */}
        <div className="rounded-[3rem] bg-gradient-to-b from-[#3a3a3c] via-[#1d1d1f] to-[#3a3a3c] p-[3px] shadow-[0_30px_80px_-20px_rgba(15,23,42,0.55)]">
          <div className="rounded-[2.9rem] bg-black p-[10px]">
            <div className="relative aspect-[9/19.5] overflow-hidden rounded-[2.3rem] bg-[#F7F8FA]">
              <div className="absolute left-1/2 top-2 z-30 h-[26px] w-[92px] -translate-x-1/2 rounded-full bg-black" />
              <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-6 pt-2.5 text-[11px] font-semibold text-ink">
                <span>9:41</span>
                <span className="flex items-center gap-1">
                  <SignalHigh size={13} /> <Wifi size={13} /> <BatteryFull size={15} />
                </span>
              </div>

              <div className="absolute inset-0 flex flex-col pt-9">
                <div className="flex-1 overflow-hidden">
                  {tab === 'calc' && (selected ? <QuoteScreen lob={selected} onBack={() => setSelected(null)} /> : <CalcScreen onPick={setSelected} />)}
                  {tab === 'wallet' && <WalletScreen />}
                  {tab === 'quotes' && <QuotesScreen />}
                  {tab === 'profile' && <ProfileScreen />}
                </div>
                <nav className="grid grid-cols-4 border-t border-black/5 bg-white/90 px-2 pb-5 pt-2 backdrop-blur">
                  <TabBtn active={tab === 'calc'} onClick={() => { setTab('calc'); setSelected(null) }} icon={Calculator} label="Calculate" />
                  <TabBtn active={tab === 'wallet'} onClick={() => setTab('wallet')} icon={Wallet} label="Wallet" />
                  <TabBtn active={tab === 'quotes'} onClick={() => setTab('quotes')} icon={FileText} label="Quotes" />
                  <TabBtn active={tab === 'profile'} onClick={() => setTab('profile')} icon={Users} label="Profile" />
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p className="mt-4 text-center text-xs text-ink-soft">Tap around — it's the real app experience ✨</p>
    </div>
  )
}

function TabBtn({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: typeof Car; label: string }) {
  return (
    <button onClick={onClick} className={`flex flex-col items-center gap-0.5 rounded-lg py-1 text-[9px] font-medium transition-colors ${active ? 'text-brand' : 'text-ink-soft'}`}>
      <Icon size={18} strokeWidth={active ? 2.4 : 1.8} />
      {label}
    </button>
  )
}

function Header({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="bg-gradient-to-br from-[#0052CC] to-[#5856D6] px-4 pb-4 pt-3 text-white">
      <p className="text-[10px] font-medium text-white/70">{sub || 'Welcome back, Vijjy 👋'}</p>
      <h3 className="text-base font-bold">{title}</h3>
    </div>
  )
}

function CalcScreen({ onPick }: { onPick: (l: (typeof LOBS)[number]) => void }) {
  return (
    <div className="flex h-full flex-col">
      <Header title="Quote any policy, in seconds" />
      <div className="grid grid-cols-2 gap-2.5 overflow-y-auto p-3">
        {LOBS.map((l, i) => (
          <button
            key={l.key}
            onClick={() => onPick(l)}
            className="animate-fade-up flex flex-col items-start gap-2 rounded-2xl bg-white p-3 text-left shadow-sm transition-transform active:scale-95"
            style={{ animationDelay: `${i * 50}ms` }}
          >
            <span className={`flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br ${l.tone} text-white`}>
              <l.icon size={18} />
            </span>
            <span className="text-xs font-semibold leading-tight text-ink">{l.label}</span>
            <span className="text-[10px] text-ink-soft">from {l.premium}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function QuoteScreen({ lob, onBack }: { lob: (typeof LOBS)[number]; onBack: () => void }) {
  const rows = [
    ['Own Damage', '₹8,940'],
    ['Third-Party', '₹2,094'],
    ['Add-ons (Zero Dep)', '₹1,446'],
  ]
  return (
    <div className="flex h-full animate-fade-in flex-col">
      <div className="flex items-center gap-2 bg-gradient-to-br from-[#0052CC] to-[#5856D6] px-3 pb-4 pt-3 text-white">
        <button onClick={onBack} className="rounded-full bg-white/15 p-1"><ChevronLeft size={16} /></button>
        <h3 className="text-sm font-bold">{lob.label} premium</h3>
      </div>
      <div className="flex-1 space-y-2.5 overflow-y-auto p-3">
        <div className="rounded-2xl bg-white p-3 shadow-sm">
          <p className="text-[10px] text-ink-soft">Total premium (incl. GST)</p>
          <p className="text-2xl font-extrabold text-brand">{lob.premium}</p>
          <div className="mt-2 space-y-1.5 border-t border-black/5 pt-2">
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between text-[11px]"><span className="text-ink-soft">{k}</span><span className="font-semibold text-ink">{v}</span></div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl bg-white p-3 shadow-sm">
          <p className="text-[11px] font-semibold text-ink">Indicative estimate</p>
          <p className="mt-0.5 text-[10px] text-ink-soft">Generate a branded PDF and share it with your customer in one tap.</p>
        </div>
        <button className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-brand py-2.5 text-xs font-semibold text-white shadow-sm active:scale-95">
          <Share2 size={14} /> Share quote
        </button>
      </div>
    </div>
  )
}

function WalletScreen() {
  const cards = [
    { n: 'Private Car · Comprehensive', e: 'Renews 14 Aug', c: 'from-blue-500 to-blue-700' },
    { n: 'Family Health · Floater', e: 'Renews 02 Nov', c: 'from-indigo-500 to-indigo-700' },
  ]
  return (
    <div className="flex h-full flex-col">
      <Header title="Policy Wallet" sub="3 policies · 1 renewal soon" />
      <div className="space-y-3 p-3">
        {cards.map((p, i) => (
          <div key={p.n} className={`animate-fade-up rounded-2xl bg-gradient-to-br ${p.c} p-3.5 text-white shadow-md`} style={{ animationDelay: `${i * 80}ms` }}>
            <p className="text-xs font-bold">{p.n}</p>
            <p className="mt-3 text-[10px] text-white/80">{p.e}</p>
          </div>
        ))}
        <button className="w-full rounded-xl border border-dashed border-brand/40 py-2.5 text-xs font-semibold text-brand">+ Scan a policy QR</button>
      </div>
    </div>
  )
}

function QuotesScreen() {
  const items = [
    ['Private Car · 2024', '₹12,480'],
    ['Health · Family', '₹18,600'],
    ['Two-Wheeler', '₹2,190'],
  ]
  return (
    <div className="flex h-full flex-col">
      <Header title="Recent quotes" sub="Saved & shareable" />
      <div className="space-y-2 p-3">
        {items.map(([t, v], i) => (
          <div key={t} className="animate-fade-up flex items-center justify-between rounded-2xl bg-white p-3 text-[11px] shadow-sm" style={{ animationDelay: `${i * 70}ms` }}>
            <span className="font-semibold text-ink">{t}</span>
            <span className="font-bold text-brand">{v}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function ProfileScreen() {
  return (
    <div className="flex h-full flex-col">
      <Header title="Rakesh" sub="Insurance advisor · Ajmer, Rajasthan" />
      <div className="space-y-2 p-3">
        {['Recent quotes', 'My customers', 'Renewals calendar', 'Settings'].map((t, i) => (
          <div key={t} className="animate-fade-up rounded-xl bg-white px-3 py-2.5 text-xs font-medium text-ink shadow-sm" style={{ animationDelay: `${i * 60}ms` }}>{t}</div>
        ))}
      </div>
    </div>
  )
}
